import {
  Children,
  cloneElement,
  isValidElement,
  useEffect,
  useState,
} from 'react';
import type { Dispatch, ReactNode, SetStateAction } from 'react';
import {
  FilterActionButton,
  FilterActionsBox,
  FilterGroup,
  FilterRow,
  SearchAndFilterStack,
  SearchRow,
} from '../styles';
import type { FilterValue } from '../FilterOption';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import DescriptionPopper from '@/components/DescriptionPopper';
import Card from '@/components/Card';
import TextField from '@/components/Input/TextField';
import SelectInput from '@/components/Input/SelectInput';
import { FilterIcon, FilterX, Info, Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Collapse from '@mui/material/Collapse';
import Skeleton from '@mui/material/Skeleton';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
export type SelectedFilters = Record<string, FilterValue>;

interface FilterOptionElementProps {
  name: string;
  field: 'toggle' | 'select' | 'date' | 'text';
  multiple?: boolean;
}

// A free-text filter picked from the text search bar's select
export interface TextSearchField {
  name: string;
  label: string;
  // Shown on an info icon while this field is picked
  hint?: string;
}

interface FiltersDisplayProps {
  children: ReactNode;
  filters: SelectedFilters;
  setFilters: Dispatch<SetStateAction<SelectedFilters>>;
  loading?: boolean;
  // Filters always shown, applied together by a Search button
  advanced?: boolean;
  // Extra controls shown before the advanced Clear/Search buttons
  actions?: ReactNode;
  // Sees the filters while they're edited, before an advanced search applies them
  onDraftChange?: (filters: SelectedFilters) => void;
  // Filters cleared when another one changes, e.g. { manufacturer: ['model'] }
  dependents?: Record<string, string[]>;
  // One text bar searching whichever of these fields is picked beside it
  textSearch?: TextSearchField[];
  // Disables the text search bar
  disabled?: boolean;
  // Optional search bar wiring; without it the search bar isn't rendered
  searchTerm?: string;
  setSearchTerm?: (term: string) => void;
  onSearch?: () => void;
  searchPlaceholder?: string;
}

function isEmptyFilterValue(value: FilterValue) {
  return (
    value === null ||
    value === undefined ||
    value === '' ||
    (Array.isArray(value) && value.length === 0)
  );
}

function FiltersDisplay({
  children,
  filters,
  setFilters,
  loading = false,
  advanced = false,
  actions,
  onDraftChange,
  dependents,
  textSearch,
  disabled = false,
  searchTerm,
  setSearchTerm,
  onSearch,
  searchPlaceholder,
}: FiltersDisplayProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.down('sm'));

  const [showFilters, setShowFilters] = useState<boolean>(false);
  // In advanced mode edits stay here until searched, then go out as one object
  const [draft, setDraft] = useState<SelectedFilters>(filters);
  const [textField, setTextField] = useState(
    () =>
      textSearch?.find((field) => field.name in filters)?.name ??
      textSearch?.[0]?.name ??
      ''
  );

  useEffect(() => {
    setDraft(filters);
  }, [filters]);

  const hasSearch = Boolean(onSearch || setSearchTerm);
  const currentFilters = advanced ? draft : filters;

  useEffect(() => {
    onDraftChange?.(currentFilters);
  }, [currentFilters, onDraftChange]);

  // Empty values are dropped, so only the selected filters are kept
  const setFilter = (name: string, value: FilterValue) => {
    (advanced ? setDraft : setFilters)((prev) => {
      const unchanged = isEmptyFilterValue(value)
        ? !(name in prev)
        : prev[name] === value;
      if (unchanged) return prev;
      const next = { ...prev };
      dependents?.[name]?.forEach((dependent) => delete next[dependent]);
      if (isEmptyFilterValue(value)) {
        delete next[name];
      } else {
        next[name] = value;
      }
      return next;
    });
  };

  // A new object, so searching again with the same filters still refetches
  const submitFilters = () => setFilters({ ...draft });

  const clearFilters = () => {
    setDraft({});
    setFilters({});
  };

  // Moves the typed value over to the newly picked field
  const changeTextField = (label: string) => {
    const next = textSearch?.find((field) => field.label === label);
    if (!next || next.name === textField) return;
    (advanced ? setDraft : setFilters)((prev) => {
      if (!(textField in prev)) return prev;
      const { [textField]: value, ...rest } = prev;
      return { ...rest, [next.name]: value };
    });
    setTextField(next.name);
  };

  const activeTextField = textSearch?.find((field) => field.name === textField);
  const textValue = currentFilters[textField];

  const textSearchBar = textSearch && activeTextField && (
    <SearchRow>
      <TextField
        width="100%"
        startIcon={<Search size={18} />}
        endIcon={
          activeTextField.hint && (
            <DescriptionPopper
              sx={{ mr: -1 }}
              description={
                <Card>
                  <Typography variant="body2" sx={{ margin: -1 }}>
                    {activeTextField.hint}
                  </Typography>
                </Card>
              }
            >
              <Info size={16} />
            </DescriptionPopper>
          )
        }
        value={typeof textValue === 'string' ? textValue : ''}
        onChange={(event) => setFilter(textField, event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && advanced) submitFilters();
        }}
        disabled={disabled}
        name="textSearch"
        id="textSearch"
      />
      <SelectInput
        options={textSearch.map((field) => field.label)}
        value={activeTextField.label}
        onChange={(event) => changeTextField(event.target.value)}
        disabled={disabled}
        width={isSm ? '100%' : '220px'}
      />
    </SearchRow>
  );

  const filterOptions = Children.map(children, (child) => {
    if (!isValidElement<FilterOptionElementProps>(child)) return child;

    const { name, multiple } = child.props;
    const emptyValue = multiple ? [] : null;

    return cloneElement(child, {
      value: currentFilters[name] ?? emptyValue,
      setValue: setFilter,
    } as Partial<FilterOptionElementProps> & {
      value: FilterValue;
      setValue: (name: string, value: FilterValue) => void;
    });
  });

  const filterRow = loading ? (
    <Skeleton variant="rounded" width="100%" height={100} />
  ) : (
    <Stack spacing={2}>
      {textSearchBar}
      <FilterRow>
        <FilterGroup>{filterOptions}</FilterGroup>
        {advanced && (
          <FilterActionsBox>
            {actions}
            <FilterActionButton
              variant="text"
              color="primary"
              onClick={clearFilters}
              startIcon={<FilterX size={16} />}
            >
              {t('common.clearFilters')}
            </FilterActionButton>
            <FilterActionButton
              variant="text"
              color="primary"
              onClick={submitFilters}
              startIcon={<Search size={16} />}
            >
              {t('common.search')}
            </FilterActionButton>
          </FilterActionsBox>
        )}
      </FilterRow>
    </Stack>
  );

  return (
    <Stack direction="column" spacing={2}>
      {(hasSearch || !advanced) && (
        <SearchAndFilterStack
          sx={hasSearch ? undefined : { justifyContent: 'flex-end' }}
        >
          {hasSearch && (
            <Box sx={{ flex: 1, width: '100%' }}>
              <SearchRow>
                <TextField
                  width="100%"
                  placeholder={
                    searchPlaceholder ?? t('listEquipments.searchPlaceholder')
                  }
                  startIcon={<Search size={18} />}
                  value={searchTerm ?? ''}
                  onChange={
                    setSearchTerm
                      ? (event) => setSearchTerm(event.target.value)
                      : undefined
                  }
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') onSearch?.();
                  }}
                  name="searchTerm"
                  id="searchTerm"
                  error={false}
                />
              </SearchRow>
            </Box>
          )}

          {/* Sized to its buttons, so the search field fills the rest of the row */}
          <FilterActionsBox sx={{ width: 'auto', flexShrink: 0 }}>
            {hasSearch && (
              <FilterActionButton
                variant="text"
                color="primary"
                disabled={false}
                onClick={onSearch}
                startIcon={<Search size={16} />}
              >
                {t('common.search')}
              </FilterActionButton>
            )}
            {!advanced && (
              <FilterActionButton
                variant="text"
                color="primary"
                disabled={false}
                onClick={() => setShowFilters(!showFilters)}
                startIcon={<FilterIcon size={16} />}
              >
                {t('listEquipments.modal.filters', 'Filtros')}
              </FilterActionButton>
            )}
          </FilterActionsBox>
        </SearchAndFilterStack>
      )}

      {advanced ? (
        filterRow
      ) : (
        <Collapse in={showFilters} timeout="auto" unmountOnExit>
          {filterRow}
        </Collapse>
      )}
    </Stack>
  );
}

export default FiltersDisplay;
